import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MessageSquareHeart, Sparkles, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import MoodChart from "@/components/MoodChart";
import ShareCard from "@/components/ShareCard";

const MOOD_LABELS = ["calm", "joyful", "tired", "anxious", "low", "angry"];

export default function Reflection() {
    const [data, setData] = useState(null);
    const [moods, setMoods] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/reflection/weekly").then((r) => setData(r.data)).catch(() => {}),
            api.get("/moods").then((r) => setMoods(r.data)).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-4xl mx-auto pt-4">
                <p className="text-xs uppercase tracking-[0.3em] text-inkSoft mb-3 font-body">weekly reflection</p>
                <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-ink mb-2">
                    a look back at your week.
                </h1>
                <p className="text-inkSoft font-body mb-10 max-w-xl">
                    a quiet summary of what you shared with Pax over the last seven days. no scores, no judgement — just a picture.
                </p>

                {loading ? (
                    <p className="text-inkSoft font-body italic">loading…</p>
                ) : (
                    <>
                        <motion.blockquote
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="border-l-2 border-terracotta pl-5 py-1 mb-10 font-journal italic text-ink text-xl md:text-2xl leading-relaxed"
                            data-testid="reflection-prompt"
                        >
                            {data?.prompt}
                        </motion.blockquote>

                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="reflection-stats">
                            <StatCard
                                icon={<MessageSquareHeart className="w-5 h-5 text-terracotta" />}
                                label="messages"
                                value={data?.messages_sent ?? 0}
                            />
                            <StatCard
                                icon={<CalendarDays className="w-5 h-5 text-terracotta" />}
                                label="journal entries"
                                value={data?.journal_entries ?? 0}
                            />
                            <StatCard
                                icon={<Sparkles className="w-5 h-5 text-terracotta" />}
                                label="memories added"
                                value={data?.memories_added ?? 0}
                            />
                            <StatCard
                                icon={<TrendingUp className="w-5 h-5 text-terracotta" />}
                                label="avg intensity"
                                value={data?.avg_intensity != null ? data.avg_intensity : "—"}
                            />
                        </div>

                        <div className="bg-white/70 border border-clayHover rounded-3xl p-6 md:p-8 mb-8">
                            <h2 className="font-heading text-xl tracking-tight text-ink mb-4">mood, 14 days</h2>
                            <MoodChart moods={moods} />
                        </div>

                        {data?.mood_distribution && Object.keys(data.mood_distribution).length > 0 && (
                            <div className="bg-white/70 border border-clayHover rounded-3xl p-6 md:p-8 mb-8" data-testid="reflection-distribution">
                                <h2 className="font-heading text-xl tracking-tight text-ink mb-4">this week's moods</h2>
                                <div className="flex flex-wrap gap-2">
                                    {MOOD_LABELS.map((m) => {
                                        const n = data.mood_distribution[m] || 0;
                                        if (!n) return null;
                                        return (
                                            <span key={m} className="text-sm bg-clay text-ink px-3 py-1.5 rounded-full font-body">
                                                {m} × {n}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <ShareCard />
                    </>
                )}
            </div>
    );
}

function StatCard({ icon, label, value }) {
    return (
        <div className="bg-white/70 border border-clayHover rounded-2xl p-5">
            <div className="w-9 h-9 rounded-xl bg-clay flex items-center justify-center mb-3">{icon}</div>
            <p className="text-xs uppercase tracking-widest text-inkSoft mb-1 font-body">{label}</p>
            <p className="font-heading text-2xl tracking-tight text-ink">{value}</p>
        </div>
    );
}
